/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { FeatureSwitchName } from "portal-ui-remote/FeatureStatusContext";
import formatUrl from "utils/formatUrl";

import api from "config/urls/api";

import { usePortalApi } from "./setup";
import { ApiError } from "./shared";

export const useInviteCompanyLinkQuery = () => {
  return usePortalApi<string, ApiError>(
    {
      url: formatUrl(api.companies.inviteLink),
      method: "GET",
    },
    {
      manual: true,
    }
  );
};

export type PlanType = "Free" | "StartUp" | "Enterprise";

export interface AccountPlan {
  currency: string;
  flatRate: number;
  interval: string;
  name: string;
  planType: PlanType;
  unitRate: number;
  unitThreshold: number;
}

interface GetFeatureStatusParams {
  featureNames: readonly string[];
}

export type GetFeatureStatusResponse = Record<FeatureSwitchName, boolean>;

export const useGetFeatureStatusQuery = ({
  featureNames,
}: GetFeatureStatusParams) => {
  return usePortalApi<GetFeatureStatusResponse, ApiError>({
    url: formatUrl(api.featureStatus),
    params: { features: featureNames },
  });
};

interface PostAboutCompanyParams {
  countryCode: string;
  jobTitle: string;
  employeeRange: string;
}

export const usePostAboutCompany = (content: PostAboutCompanyParams) => {
  return usePortalApi<unknown, ApiError>(
    {
      url: formatUrl(api.onboarding.aboutCompany),
      method: "POST",
      data: content,
    },
    {
      manual: true,
    }
  );
};

export interface GetConnectionsReportResponse {
  linked: number;
  deAuthorized: number;
  pendingAuth: number;
  noConnection: number;
  unlinked: number;
}

export const useGetConnectionsReportQuery = () => {
  return usePortalApi<GetConnectionsReportResponse, ApiError>({
    url: api.dashboard.connections.report,
    method: "GET",
  });
};

export interface GetCumulativeCreatedCompaniesParams {
  period: number;
}

type GetCumulativeCreatedCompaniesResponse = Record<string, number>;

export const useGetCumulativeCreatedCompaniesQuery = ({
  period,
}: GetCumulativeCreatedCompaniesParams) => {
  return usePortalApi<GetCumulativeCreatedCompaniesResponse, ApiError>({
    url: api.dashboard.companies.cumulativeCreated,
    method: "GET",
    params: { periodInDays: period },
  });
};

interface SigninMessage {
  message: string;
}

export const useGetSigninMessage = () => {
  return usePortalApi<SigninMessage, ApiError>({
    url: formatUrl(api.signinmessages),
  });
};

export type IntegrationsCountsKeys =
  | "accounting"
  | "banking"
  | "commerce"
  | "bankFeeds"
  | "expense"
  | "otherIntegrations"
  | "dataTypes";

export interface IntegrationCountsValues {
  enabled: number;
  total: number;
}

export type IntegrationsCounts = Record<
  IntegrationsCountsKeys,
  IntegrationCountsValues
>;

export const useGetIntegrationsCounts = () => {
  return usePortalApi<IntegrationsCounts, ApiError>({
    url: api.integrations.counts,
    method: "GET",
  });
};

type GetWebhooksDashboardResponse = {
  url: string;
};

export const useGetWebhooksDashboard = () => {
  return usePortalApi<GetWebhooksDashboardResponse, ApiError>({
    url: api.webhooks.dashboard,
    method: "GET",
  });
};

export type GetSubscriptionStatusResponse = {
  subscriptionStatus: string;
};

export const useGetSubscriptionStatus = () => {
  return usePortalApi<GetSubscriptionStatusResponse, ApiError>(
    {
      url: api.account.subscriptionStatus,
      method: "GET",
    },
    { manual: true }
  );
};
